import { Suspense, lazy } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import { PATH_DASHBOARD } from 'src/routes/paths';
import MainLayout from 'src/layouts/main/MainLayout';
import AuthGuard from 'src/components/guard/AuthGuard';
import GuestGuard from 'src/components/guard/GuestGuard';
import LoadingScreen from 'src/components/LoadingScreen';
import ProfileGuard from 'src/components/guard/ProfileGuard';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';

const Loadable = (Component) => (props) =>
  (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );

const Login = Loadable(lazy(() => import('src/pages/authentication/Login')));

const HeyatAdminRegister = Loadable(
  lazy(() => import('src/pages/authentication/HeyatAdminRegister'))
);

const ResetPassword = Loadable(
  lazy(() => import('src/pages/authentication/ResetPassword'))
);

const Overview = Loadable(
  lazy(() => import('src/pages/dashboard/overview/Overview'))
);

const Profile = Loadable(
  lazy(() => import('src/pages/dashboard/profile/Profile'))
);

const User = Loadable(lazy(() => import('src/pages/dashboard/users/UserList')));

const AddUser = Loadable(
  lazy(() => import('src/pages/dashboard/users/UserCreate'))
);

const Language = Loadable(
  lazy(() => import('src/pages/dashboard/language/LanguageList'))
);

const SpeakerSinger = Loadable(
  lazy(() => import('src/pages/dashboard/speaker-singer/SpeakerSingerList'))
);

const AddSpeakerSinger = Loadable(
  lazy(() => import('src/pages/dashboard/speaker-singer/SpeakerSingerCreate'))
);

const Heyat = Loadable(
  lazy(() => import('src/pages/dashboard/heyat/HeyatList'))
);

const AddHeyat = Loadable(
  lazy(() => import('src/pages/dashboard/heyat/HeyatCreate'))
);

const HeyatProfile = Loadable(
  lazy(() => import('src/pages/dashboard/heyat/HeyatProfile'))
);

const OccasionsList = Loadable(
  lazy(() => import('src/pages/dashboard/occasions/OccasionsList'))
);

const OccasionCreate = Loadable(
  lazy(() => import('src/pages/dashboard/occasions/OccasionCreate'))
);

const EventsCalendar = Loadable(
  lazy(() => import('src/pages/dashboard/events/EventsCalendar'))
);

const Haram = Loadable(
  lazy(() => import('src/pages/dashboard/harams/HaramList'))
);

const AddHaram = Loadable(
  lazy(() => import('src/pages/dashboard/harams/HaramCreate'))
);

const Quran = Loadable(
  lazy(() => import('src/pages/dashboard/qurans/QuranList'))
);

const AddQuran = Loadable(
  lazy(() => import('src/pages/dashboard/qurans/QuranCreate'))
);

const News = Loadable(lazy(() => import('src/pages/dashboard/news/NewsList')));

const AddNews = Loadable(
  lazy(() => import('src/pages/dashboard/news/NewsCreate'))
);

const NewsCategory = Loadable(
  lazy(() => import('src/pages/dashboard/news/NewsCategoryList'))
);

const AddNewsCategory = Loadable(
  lazy(() => import('src/pages/dashboard/news/NewsCategoryCreate'))
);

const ContactUs = Loadable(
  lazy(() => import('src/pages/dashboard/contactUs/ContactUsList'))
);

const Report = Loadable(
  lazy(() => import('src/pages/dashboard/reports/ReportList'))
);

const Slider = Loadable(
  lazy(() => import('src/pages/dashboard/sliders/SliderList'))
);

const AddSlider = Loadable(
  lazy(() => import('src/pages/dashboard/sliders/SliderCreate'))
);

const Page = Loadable(lazy(() => import('src/pages/dashboard/pages/PageList')));

const AddPage = Loadable(
  lazy(() => import('src/pages/dashboard/pages/PageCreate'))
);

const Gallery = Loadable(
  lazy(() => import('src/pages/dashboard/gallery/GalleryList'))
);

const Notification = Loadable(
  lazy(() => import('src/pages/dashboard/notifications/NotificationList'))
);

const AddNotification = Loadable(
  lazy(() => import('src/pages/dashboard/notifications/NotificationCreate'))
);

const Server = Loadable(
  lazy(() => import('src/pages/dashboard/server/Server'))
);

const Archive = Loadable(
  lazy(() => import('src/pages/dashboard/archive/ArchiveList'))
);

const AdminHeyatArchive = Loadable(
  lazy(() => import('src/pages/dashboard/archive/AdminHeyatArchiveList'))
);

const Advertisement = Loadable(
  lazy(() => import('src/pages/dashboard/advertisement/AdvertisementList'))
);

const AddAdvertisement = Loadable(
  lazy(() => import('src/pages/dashboard/advertisement/AdvertisementCreate'))
);

const LiveProgram = Loadable(
  lazy(() => import('src/pages/dashboard/livePrograms/LiveProgramsList'))
);

const AdminHeyatLiveProgram = Loadable(
  lazy(
    () => import('src/pages/dashboard/livePrograms/AdminHeyatLiveProgramsList')
  )
);

const ConfigList = Loadable(
  lazy(() => import('src/pages/dashboard/config/ConfigList'))
);

const TagsList = Loadable(
  lazy(() => import('src/pages/dashboard/tags/TagsList'))
);

const AddTag = Loadable(
  lazy(() => import('src/pages/dashboard/tags/TagCreate'))
);

const AddConfig = Loadable(
  lazy(() => import('src/pages/dashboard/config/ConfigCreate'))
);

const FaqList = Loadable(lazy(() => import('src/pages/dashboard/faq/FaqList')));

const FaqCreate = Loadable(
  lazy(() => import('src/pages/dashboard/faq/FaqCreate'))
);

const ThemeSetting = Loadable(
  lazy(() => import('src/pages/dashboard/theme/Theme'))
);

const AuthorizationRequired = Loadable(
  lazy(() => import('src/pages/errors/AuthorizationRequired'))
);

const NotFound = Loadable(lazy(() => import('src/pages/errors/NotFound')));

const ServerError = Loadable(
  lazy(() => import('src/pages/errors/ServerError'))
);

export default function Router() {
  return useRoutes([
    { path: '/', element: <Navigate to={PATH_DASHBOARD.overview} /> },
    {
      path: 'authentication',
      element: (
        <GuestGuard>
          <MainLayout />
        </GuestGuard>
      ),
      children: [
        {
          path: 'login',
          element: <Login />,
        },
        {
          path: 'heyat-admin-signup',
          element: <HeyatAdminRegister />,
        },
        {
          path: 'reset-password',
          element: <ResetPassword />,
        },
      ],
    },
    {
      path: 'dashboard/heyats',
      element: <DashboardLayout />,
      children: [
        {
          path: 'profile',
          element: <HeyatProfile />,
        },
        {
          path: 'archive',
          element: <AdminHeyatArchive />,
        },
        {
          path: 'live',
          element: <AdminHeyatLiveProgram />,
        },
      ],
    },
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <ProfileGuard>
            <DashboardLayout />
          </ProfileGuard>
        </AuthGuard>
      ),
      children: [
        {
          path: 'overview',
          element: <Overview />,
        },
        {
          path: 'profile',
          element: <Profile />,
        },
        {
          path: 'occasions',
          children: [
            {
              path: 'list',
              element: <OccasionsList />,
            },
            {
              path: 'create',
              element: <OccasionCreate />,
            },
            {
              path: ':id/edit',
              element: <OccasionCreate />,
            },
          ],
        },
        {
          path: 'events',
          children: [
            {
              path: 'calendar',
              element: <EventsCalendar />,
            },
          ],
        },
        {
          path: 'language',
          element: <Language />,
        },
        {
          path: 'users',
          children: [
            {
              path: 'list',
              element: <User />,
            },
            {
              path: 'create',
              element: <AddUser />,
            },
            {
              path: ':id/edit',
              element: <AddUser />,
            },
          ],
        },
        {
          path: 'speaker-singer',
          children: [
            {
              path: 'list',
              element: <SpeakerSinger />,
            },
            {
              path: 'create',
              element: <AddSpeakerSinger />,
            },
            {
              path: ':id/edit',
              element: <AddSpeakerSinger />,
            },
          ],
        },
        {
          path: 'heyats',
          children: [
            {
              path: 'list',
              element: <Heyat />,
            },
            {
              path: 'list/:filtered',
              element: <Heyat />,
            },
            {
              path: 'create',
              element: <AddHeyat />,
            },
            {
              path: ':id/edit',
              element: <AddHeyat />,
            },
          ],
        },
        {
          path: 'harams',
          children: [
            {
              path: 'list',
              element: <Haram />,
            },
            {
              path: 'create',
              element: <AddHaram />,
            },
            {
              path: ':id/edit',
              element: <AddHaram />,
            },
          ],
        },
        {
          path: 'qurans',
          children: [
            {
              path: 'list',
              element: <Quran />,
            },
            {
              path: 'create',
              element: <AddQuran />,
            },
            {
              path: ':id/edit',
              element: <AddQuran />,
            },
          ],
        },
        {
          path: 'news',
          children: [
            {
              path: 'list',
              element: <News />,
            },
            {
              path: 'create',
              element: <AddNews />,
            },
            {
              path: ':id/edit',
              element: <AddNews />,
            },
            {
              path: 'category/list',
              element: <NewsCategory />,
            },
            {
              path: 'category/create',
              element: <AddNewsCategory />,
            },
            {
              path: 'category/:id/edit',
              element: <AddNewsCategory />,
            },
          ],
        },
        {
          path: 'contact-us',
          children: [
            {
              path: 'list',
              element: <ContactUs />,
            },
          ],
        },
        {
          path: 'reports',
          children: [
            {
              path: 'list',
              element: <Report />,
            },
            {
              path: 'list/:id/:type',
              element: <Report />,
            },
          ],
        },
        {
          path: 'sliders',
          children: [
            {
              path: 'list',
              element: <Slider />,
            },
            {
              path: 'create',
              element: <AddSlider />,
            },
            {
              path: ':id/edit',
              element: <AddSlider />,
            },
          ],
        },
        {
          path: 'pages',
          children: [
            {
              path: 'list',
              element: <Page />,
            },
            {
              path: ':id/edit',
              element: <AddPage />,
            },
          ],
        },
        {
          path: 'gallery',
          children: [
            {
              path: 'list',
              element: <Gallery />,
            },
            {
              path: 'list/:type',
              element: <Gallery />,
            },
          ],
        },
        {
          path: 'notifications',
          children: [
            {
              path: 'list',
              element: <Notification />,
            },
            {
              path: 'create',
              element: <AddNotification />,
            },
            {
              path: ':id/edit',
              element: <AddNotification />,
            },
          ],
        },
        {
          path: 'server',
          element: <Server />,
        },
        {
          path: 'theme',
          element: <ThemeSetting />,
        },
        {
          path: 'archive',
          children: [
            {
              path: 'list',
              element: <Archive />,
            },
          ],
        },
        {
          path: 'advertisements',
          children: [
            {
              path: 'list',
              element: <Advertisement />,
            },
            {
              path: ':id/edit',
              element: <AddAdvertisement />,
            },
          ],
        },
        {
          path: 'live-program',
          children: [
            {
              path: 'list',
              element: <LiveProgram />,
            },
          ],
        },
        {
          path: 'config',
          children: [
            {
              path: 'list',
              element: <ConfigList />,
            },
            {
              path: 'create',
              element: <AddConfig />,
            },
            {
              path: ':id/edit',
              element: <AddConfig />,
            },
          ],
        },
        {
          path: 'tags',
          children: [
            {
              path: 'list',
              element: <TagsList />,
            },
            {
              path: 'create',
              element: <AddTag />,
            },
            {
              path: ':id/edit',
              element: <AddTag />,
            },
          ],
        },
        {
          path: 'faq',
          children: [
            {
              path: 'list',
              element: <FaqList />,
            },
            {
              path: 'create',
              element: <FaqCreate />,
            },
            {
              path: ':id/edit',
              element: <FaqCreate />,
            },
          ],
        },
      ],
    },
    {
      path: '*',
      element: <MainLayout />,
      children: [
        {
          path: '401',
          element: <AuthorizationRequired />,
        },
        {
          path: '404',
          element: <NotFound />,
        },
        {
          path: '500',
          element: <ServerError />,
        },
        {
          path: '*',
          element: <NotFound />,
        },
      ],
    },
  ]);
}
